import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  confirmPayment,
  createCheckoutSession,
  setCheckoutManifest,
  clearCheckoutManifest,
  getMultibancoMethod,
  isMultibancoActive,
} from '../../redux/actions/payments';
import { cancelBooking, getBrandEvents } from '../../redux/actions/bookings';
import { fetchBrandBillingDetails } from '../../redux/actions/brands';
import { canCancelBooking, canPayBooking } from 'utils/bookingActionability';
import { useAlert } from 'context/AlertContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import {
  NoSymbolIcon,
  DocumentIcon,
  ArrowTopRightOnSquareIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { computeBookingPricingFromTotal } from 'utils/bookingPricing';
import FeeBoundsTooltip from 'components/FeeBoundsTooltip';

// Fired by the header PayBookingButton (T2B-393); the mounted BookingStatus
// for the same booking picks it up and starts its checkout flow.
const START_PAYMENT_EVENT = 't2b:start-booking-payment';

const RECENT_PAYMENT_KEY = 'recentPaymentBookingId';
const RECENT_PAYMENT_TTL = 5 * 60 * 1000; // 5 minutes
const PAYMENT_POLL_INTERVAL = 5000; // 5 seconds
const PAYMENT_POLL_MAX = 2 * 60 * 1000; // stop after 2 minutes

function getEventWindow(dateObj) {
  if (!dateObj || !dateObj.type) return { start: null, end: null };

  if (dateObj.type === 'fixed') {
    const start = moment(
      `${dateObj.startDate} ${dateObj.openHours || '00:00'}`,
      'YYYY-MM-DD HH:mm'
    );
    const end = moment(
      `${dateObj.endDate} ${dateObj.closeHours || '23:59'}`,
      'YYYY-MM-DD HH:mm'
    );
    return {
      start: start.isValid() ? start : null,
      end: end.isValid() ? end : null,
    };
  }

  if (
    dateObj.type === 'segmented' &&
    Array.isArray(dateObj.days) &&
    dateObj.days.length
  ) {
    const days = [...dateObj.days]
      .filter((d) => d?.date)
      .sort((a, b) => a.date.localeCompare(b.date));
    if (!days.length) return { start: null, end: null };
    const first = days[0];
    const last = days[days.length - 1];
    const start = moment(
      `${first.date} ${first.openHours || '00:00'}`,
      'YYYY-MM-DD HH:mm'
    );
    const end = moment(
      `${last.date} ${last.closeHours || '23:59'}`,
      'YYYY-MM-DD HH:mm'
    );
    return {
      start: start.isValid() ? start : null,
      end: end.isValid() ? end : null,
    };
  }

  return { start: null, end: null };
}

const SESSION_MANIFEST_PREFIX = 'easypayCheckoutManifest:';
const SESSION_MANIFEST_ENV_PREFIX = 'easypayCheckoutEnv:';
const LEGACY_SESSION_MANIFEST_KEY = 'easypayCheckoutManifest';
const LEGACY_SESSION_MANIFEST_ENV_KEY = 'easypayCheckoutEnv';

const readCachedManifest = (bookingId) => {
  if (!bookingId) return null;
  try {
    const raw = sessionStorage.getItem(`${SESSION_MANIFEST_PREFIX}${bookingId}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const buildPendingMbManifest = (mb) => ({
  id: mb?.checkoutId,
  checkoutId: mb?.checkoutId,
  _pendingPayment: true,
  paymentMethod: 'mb',
  mbEntity: mb?.entity,
  mbReference: mb?.reference,
  mbAmount: mb?.value,
  mbExpiresAt: mb?.expiration_date,
  payment: {
    method: {
      type: 'mb',
      entity: mb?.entity,
      reference: mb?.reference,
      value: mb?.value,
      expiration_date: mb?.expiration_date,
    },
  },
});

const getBookingBillingInfo = (booking) =>
  booking?.billingInfo ||
  booking?.BillingInfo ||
  booking?.activePayment?.billingInfo ||
  booking?.Payment?.billingInfo ||
  null;

const normalizeOptionalBillingField = (value) => {
  const trimmed = String(value || '').trim();
  return trimmed || null;
};

// Rendered by EventManagerModal in the tab-bar row (T2B-378) so the action
// sits at the top of the modal instead of floating inside the tab panel.
export const CancelBookingButton = ({ event }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showAlert, confirm } = useAlert();
  const [isCancelling, setIsCancelling] = useState(false);
  const booking = event?.booking;

  if (!canCancelBooking(booking)) return null;

  const handleCancelBooking = async () => {
    if (!booking?.id || isCancelling) return;
    const ok = await confirm({
      type: 'confirm',
      title: t('dashboard.cancel_booking_title') || 'Cancel this booking request?',
      message:
        t('dashboard.cancel_booking_message') ||
        "This will release the space and notify the promoter. You won't be able to undo this.",
      confirmLabel: t('dashboard.cancel_booking_confirm') || 'Yes, cancel',
      confirmIntent: 'danger',
      cancelLabel: t('common.back') || 'Back',
    });
    if (!ok) return;

    setIsCancelling(true);
    try {
      await dispatch(cancelBooking(booking.id));
      try { dispatch(getBrandEvents()); } catch {}
      showAlert({
        type: 'success',
        title: t('dashboard.cancel_booking_success_title') || 'Booking cancelled',
        message:
          t('dashboard.cancel_booking_success_message') ||
          'Your booking request has been cancelled.',
        primaryAction: { label: t('common.ok') || 'OK' },
      });
    } catch (err) {
      const data = err?.response?.data || {};
      showAlert({
        type: 'error',
        title: t('dashboard.cancel_booking_error_title') || 'Could not cancel booking',
        message:
          data.message ||
          err?.message ||
          t('dashboard.cancel_booking_error_message') ||
          'Something went wrong while cancelling this booking. Please try again.',
        primaryAction: { label: t('common.ok') || 'OK' },
      });
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCancelBooking}
      disabled={isCancelling}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold text-red-600 bg-white border border-red-200 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
    >
      {isCancelling ? (
        <ClipLoader size={12} color="#dc2626" />
      ) : (
        <NoSymbolIcon className="w-4 h-4" />
      )}
      <span>
        {isCancelling
          ? t('common.loading') || 'Loading…'
          : t('dashboard.cancel_booking') || 'Cancel request'}
      </span>
    </button>
  );
};

// Blue twin of the bottom "Pay Now" CTA, rendered by EventManagerModal next to
// CancelBookingButton in the tab-bar row (T2B-393). Clicking it asks the
// mounted BookingStatus (same booking) to start its checkout flow.
export const PayBookingButton = ({ event }) => {
  const { t } = useTranslation();
  const booking = event?.booking;

  if (!canPayBooking(booking)) return null;

  return (
    <button
      type="button"
      onClick={() =>
        window.dispatchEvent(
          new CustomEvent(START_PAYMENT_EVENT, {
            detail: { bookingId: booking?.id },
          })
        )
      }
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold text-white bg-blue-500 border border-blue-500 hover:bg-blue-600 hover:border-blue-600 shadow-sm"
    >
      <CreditCardIcon className="w-4 h-4" />
      <span>{t('dashboard.pay_now')}</span>
    </button>
  );
};

const BookingStatus = ({ event, user }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentSource = searchParams.get('source');
  const { showAlert } = useAlert();

  const booking = event?.booking;
  const status = booking?.status || 'Pending';
  const paymentStatus = booking?.paymentStatus || '';
  const isPaid =
    status === 'Paid' || String(paymentStatus).toLowerCase() === 'paid';
  const { loading } = useSelector((state) => state.payments);
  // Fallback for the billing step: what this booking already captured, else the
  // brand's saved billing details (dashboard → Payment Details). Keeps the
  // fields pre-filled instead of retyped for every booking.
  const brandBillingDetails = useSelector((s) => s.brands.billingDetails);
  const savedBillingInfo = getBookingBillingInfo(booking) || brandBillingDetails;
  const [paymentStep, setPaymentStep] = useState('summary');
  const [billingForm, setBillingForm] = useState({
    name: savedBillingInfo?.name || user?.username || '',
    nif: savedBillingInfo?.nif || '',
    address: savedBillingInfo?.address || '',
    contact: savedBillingInfo?.contact || '',
  });
  const [billingErrors, setBillingErrors] = useState({});
  const [checkoutError, setCheckoutError] = useState(null);
  const mbRepairAttemptedRef = useRef(new Set());

  useEffect(() => {
    setPaymentStep('summary');
    setBillingErrors({});
    setCheckoutError(null);
    setBillingForm({
      name: savedBillingInfo?.name || user?.username || '',
      nif: savedBillingInfo?.nif || '',
      address: savedBillingInfo?.address || '',
      contact: savedBillingInfo?.contact || '',
    });
  }, [
    booking?.id,
    savedBillingInfo?.name,
    savedBillingInfo?.nif,
    savedBillingInfo?.address,
    savedBillingInfo?.contact,
    user?.username,
  ]);

  // Pull the brand's saved billing details in before the billing step is
  // reached, so the pre-fill is already there when they click Pay. The action
  // no-ops once loaded, so mounting several booking cards costs one request.
  useEffect(() => {
    if (user?.role !== 'brand' || isPaid) return;
    dispatch(fetchBrandBillingDetails());
  }, [dispatch, user?.role, isPaid]);

  // Detect an active Multibanco reference for this booking. While one exists,
  // the backend will REUSE the same session on /checkout — a second "Pay"
  // click is a no-op from the user's POV, so we disable it and surface the
  // ATM reference instead.
  const pendingMultibanco = useMemo(() => {
    if (!booking?.id || isPaid) return null;
    const apiMb = getMultibancoMethod(booking.activePayment);
    if (apiMb && isMultibancoActive(booking.activePayment)) return apiMb;

    const cached = readCachedManifest(booking.id);
    if (!cached) return null;
    const mb = getMultibancoMethod(cached);
    if (!mb) return null;
    return isMultibancoActive(cached) ? mb : null;
  }, [booking?.id, booking?.activePayment, isPaid]);

  useEffect(() => {
    if (!booking?.id || isPaid || !pendingMultibanco) return;
    if (!pendingMultibanco.entity && !pendingMultibanco.reference) return;

    const apiMb = getMultibancoMethod(booking.activePayment);
    if (apiMb?.entity || apiMb?.reference) return;

    const repairKey = [
      booking.id,
      pendingMultibanco.checkoutId,
      pendingMultibanco.entity || '',
      pendingMultibanco.reference || '',
    ].join(':');
    if (mbRepairAttemptedRef.current.has(repairKey)) return;
    mbRepairAttemptedRef.current.add(repairKey);

    let cancelled = false;
    const persistCachedMb = async () => {
      try {
        await dispatch(
          confirmPayment({
            bookingId: booking.id,
            checkoutId: pendingMultibanco.checkoutId,
            checkoutInfo: buildPendingMbManifest(pendingMultibanco),
          })
        );
        if (!cancelled) dispatch(getBrandEvents());
      } catch {
        // Keep the local reference visible; this is a best-effort DB repair.
      }
    };

    persistCachedMb();
    return () => {
      cancelled = true;
    };
  }, [booking?.id, booking?.activePayment, isPaid, pendingMultibanco, dispatch]);

  const blockedActiveCheckout =
    !pendingMultibanco &&
    !isPaid &&
    booking?.activePayment?.checkoutId &&
    booking.activePayment?.status === 'active' &&
    booking.activePayment?.startable === false;

  // The booking's CREATION date picks the fee era: totalAmount is priced once,
  // when the booking is made, and the checkout charges exactly that stored
  // figure later. A booking made before the €50 fee ceiling shipped therefore
  // carries an uncapped total even if it is paid afterwards, and has to be read
  // back with that rule or the breakdown shown here won't add up.
  const pricing = useMemo(
    () =>
      computeBookingPricingFromTotal(booking?.totalAmount, {
        at: booking?.createdAt,
      }),
    [booking?.totalAmount, booking?.createdAt]
  );

  const fmtEUR = (n) =>
    new Intl.NumberFormat(i18n?.language || 'pt-PT', {
      style: 'currency',
      currency: 'EUR',
    }).format(Number.isFinite(n) ? n : 0);

  // --- event window guards (defensive in UI) ---
  const { start: eventStart, end: eventEnd } = useMemo(
    () => getEventWindow(event?.date),
    [event?.date]
  );
  const nowMoment = moment();
  const isEventOver = !!(eventEnd && nowMoment.isSameOrAfter(eventEnd));

  // --- countdown to booking.expireAt (decision/payment deadline) ---
  const expireAt = useMemo(
    () => (booking?.expireAt ? moment(booking.expireAt) : null),
    [booking?.expireAt]
  );

  const [now, setNow] = useState(moment());
  const pendingMbExpiresAt = useMemo(
    () =>
      pendingMultibanco?.expiration_date
        ? moment(pendingMultibanco.expiration_date)
        : null,
    [pendingMultibanco?.expiration_date]
  );

  useEffect(() => {
    if (!expireAt && !pendingMbExpiresAt) return;
    const id = setInterval(() => setNow(moment()), 1000);
    return () => clearInterval(id);
  }, [expireAt, pendingMbExpiresAt]);

  const isOverdue = expireAt ? expireAt.isBefore(now) : false;
  const timeLeftMs = expireAt ? Math.max(0, expireAt.diff(now)) : null;
  const pendingMbIsExpired =
    pendingMbExpiresAt?.isValid() && pendingMbExpiresAt.isSameOrBefore(now);
  const pendingMbTimeLeftMs =
    pendingMbExpiresAt?.isValid()
      ? Math.max(0, pendingMbExpiresAt.diff(now))
      : null;

  const fmtTimeLeft = () => {
    if (!expireAt) return null;
    if (isOverdue) return t('dashboard.manager_modal.expired');
    const dur = moment.duration(timeLeftMs);
    const d = Math.floor(dur.asDays());
    const h = dur.hours();
    const m = dur.minutes();
    const s = dur.seconds();
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const fmtPendingMbTimeLeft = () => {
    if (!pendingMbExpiresAt?.isValid()) return null;
    if (pendingMbIsExpired) return t('dashboard.manager_modal.expired');
    const dur = moment.duration(pendingMbTimeLeftMs);
    const d = Math.floor(dur.asDays());
    const h = dur.hours();
    const m = dur.minutes();
    const s = dur.seconds();
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const fmtDeadlineDate = (m) =>
    m && m.isValid() ? m.local().format('LLL') : null;

  // --- recent-payment detection ---
  // After checkout, a sessionStorage flag tells us payment was just submitted.
  // While the webhook hasn't confirmed it, we show a processing indicator
  // and poll getBrandEvents() so the prop updates when status flips to Paid.
  const isPaymentProcessing = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(RECENT_PAYMENT_KEY);
      if (!raw) return false;
      const data = JSON.parse(raw);
      if (String(data.bookingId) !== String(booking?.id)) return false;
      if (Date.now() - data.timestamp > RECENT_PAYMENT_TTL) {
        sessionStorage.removeItem(RECENT_PAYMENT_KEY);
        return false;
      }
      return !isPaid; // still processing if not yet Paid
    } catch {
      return false;
    }
  }, [booking?.id, isPaid]);

  // Clear flag once status becomes Paid
  useEffect(() => {
    if (isPaid) {
      try { sessionStorage.removeItem(RECENT_PAYMENT_KEY); } catch {}
    }
  }, [isPaid]);

  // On mount / when the booking changes, if we just came back from checkout
  // for THIS booking, do one immediate refetch so the status reflects the
  // latest server state without waiting for the poll interval below.
  const mountRefetchedRef = useRef(false);
  useEffect(() => {
    if (mountRefetchedRef.current || !booking?.id) return;
    try {
      const raw = sessionStorage.getItem(RECENT_PAYMENT_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (String(data.bookingId) !== String(booking.id)) return;
      if (Date.now() - data.timestamp > RECENT_PAYMENT_TTL) {
        sessionStorage.removeItem(RECENT_PAYMENT_KEY);
        return;
      }
      mountRefetchedRef.current = true;
      dispatch(getBrandEvents());
    } catch {}
  }, [booking?.id, dispatch]);

  // Poll for fresh booking data while payment is processing
  useEffect(() => {
    if (!isPaymentProcessing) return;

    const interval = setInterval(() => {
      dispatch(getBrandEvents());
    }, PAYMENT_POLL_INTERVAL);

    // Safety cap: stop polling after PAYMENT_POLL_MAX
    const timeout = setTimeout(() => {
      clearInterval(interval);
      try { sessionStorage.removeItem(RECENT_PAYMENT_KEY); } catch {}
      // One final fetch so the re-render clears the cached isPaymentProcessing memo
      dispatch(getBrandEvents());
    }, PAYMENT_POLL_MAX);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isPaymentProcessing, dispatch]);

  const canPay =
    status === 'Approved' &&
    !isPaid &&
    !isOverdue &&
    !isEventOver &&
    !isPaymentProcessing &&
    !pendingMultibanco &&
    !blockedActiveCheckout;

  const goToCheckoutPage = (manifestId) => {
    const checkoutParams = new URLSearchParams({
      bookingId: booking?.id,
      eventId: event?.id,
      amount: booking?.totalAmount,
      ...(manifestId ? { id: manifestId } : {}),
      ...(paymentSource && { source: paymentSource }),
    });
    navigate(`/checkout?${checkoutParams.toString()}`);
  };

  const viewPendingMultibanco = () => {
    const manifest = buildPendingMbManifest(pendingMultibanco);
    dispatch(setCheckoutManifest(manifest));
    try {
      sessionStorage.setItem(
        `${SESSION_MANIFEST_PREFIX}${booking?.id}`,
        JSON.stringify(manifest)
      );
    } catch {}
    goToCheckoutPage(pendingMultibanco?.checkoutId || null);
  };

  const handleStartCheckout = () => {
    if (!canPay || loading) return;
    setPaymentStep('billing');
    setBillingErrors({});
    setCheckoutError(null);
  };

  // Header PayBookingButton (T2B-393): start checkout and bring the payment
  // area into view. The ref indirection keeps the listener stable while the
  // handler closure changes every render.
  const paymentSectionRef = useRef(null);
  const startPaymentRef = useRef(null);
  startPaymentRef.current = () => {
    handleStartCheckout();
    requestAnimationFrame(() => {
      paymentSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    });
  };

  useEffect(() => {
    const bookingId = booking?.id;
    if (!bookingId) return;
    const onStartPayment = (e) => {
      if (String(e?.detail?.bookingId) !== String(bookingId)) return;
      startPaymentRef.current?.();
    };
    window.addEventListener(START_PAYMENT_EVENT, onStartPayment);
    return () => window.removeEventListener(START_PAYMENT_EVENT, onStartPayment);
  }, [booking?.id]);

  const updateBillingField = (field, value) => {
    setBillingForm((prev) => ({ ...prev, [field]: value }));
    setBillingErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setCheckoutError(null);
  };

  const buildBillingInfo = () => ({
    name: String(billingForm.name || '').trim(),
    nif: String(billingForm.nif || '').trim(),
    address: normalizeOptionalBillingField(billingForm.address),
    contact: normalizeOptionalBillingField(billingForm.contact),
  });

  const validateBillingInfo = (billingInfo) => {
    const nextErrors = {};
    if (!billingInfo.name) {
      nextErrors.name = t('checkout.billing_name_required');
    }
    if (!billingInfo.nif) {
      nextErrors.nif = t('checkout.billing_nif_required');
    }
    setBillingErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleBillingSubmit = (e) => {
    e.preventDefault();
    if (!canPay || loading) return;

    const billingInfo = buildBillingInfo();
    if (!validateBillingInfo(billingInfo)) return;

    handleCheckout(billingInfo);
  };

  const handleCheckout = async (billingInfo) => {
    if (!canPay || loading) return;
    setCheckoutError(null);

    // Guard: never open checkout with a missing/invalid amount. The backend
    // now derives orderValue server-side, but a bad totalAmount here means the
    // booking prop is stale — ask the user to refresh instead of mispricing.
    const amount = Number(booking?.totalAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setCheckoutError(
        t('alert_dialog.checkout.amount_unavailable_message') ||
          'We could not load the payment amount. Please refresh and try again.'
      );
      showAlert({
        type: 'error',
        title:
          t('alert_dialog.checkout.amount_unavailable_title') ||
          'Payment amount unavailable',
        message:
          t('alert_dialog.checkout.amount_unavailable_message') ||
          'We could not load the payment amount. Please refresh and try again.',
        primaryAction: {
          label: t('common.ok') || 'OK',
        },
      });
      return;
    }

    // Never carry a prior booking's manifest into a new session.
    dispatch(clearCheckoutManifest());
    try {
      sessionStorage.removeItem(LEGACY_SESSION_MANIFEST_KEY);
      sessionStorage.removeItem(LEGACY_SESSION_MANIFEST_ENV_KEY);
      if (booking?.id) {
        sessionStorage.removeItem(`${SESSION_MANIFEST_PREFIX}${booking.id}`);
        sessionStorage.removeItem(`${SESSION_MANIFEST_ENV_PREFIX}${booking.id}`);
      }
    } catch {}

    const checkoutData = {
      customerName: user.username,
      customerEmail: user.email,
      orderItems: [
        {
          description: `booking-${booking?.id}`,
          quantity: 1,
          key: booking?.id || 'booking-item',
          value: amount,
        },
      ],
      orderKey: `booking-${booking?.id}`,
      orderValue: amount,
      billingInfo,
    };

    try {
      const manifest = await dispatch(createCheckoutSession(checkoutData));
      dispatch(setCheckoutManifest(manifest));
      // _reused:true is informational only — same navigation as a fresh 201.
      goToCheckoutPage(manifest?.id);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data || {};

      if (status === 400 && data.code === 'BILLING_INFO_REQUIRED') {
        setPaymentStep('billing');
        setCheckoutError(
          data.message ||
            t('alert_dialog.checkout.billing_info_required_message') ||
            t('checkout.billing_required_error')
        );
        return;
      }

      if (status === 403 && data.code === 'FORBIDDEN') {
        setCheckoutError(
          data.message ||
            t('alert_dialog.checkout.forbidden_message') ||
            'You are not allowed to pay for this booking.'
        );
        showAlert({
          type: 'error',
          title:
            t('alert_dialog.checkout.forbidden_title') || 'Not allowed',
          message:
            data.message ||
            t('alert_dialog.checkout.forbidden_message') ||
            'You are not allowed to pay for this booking.',
          primaryAction: { label: t('common.ok') || 'OK' },
        });
        return;
      }

      if (status === 409 && data.code === 'ALREADY_PAID') {
        // Not an error — refresh data so the UI flips to the Paid view.
        try { dispatch(getBrandEvents()); } catch {}
        showAlert({
          type: 'success',
          title: t('alert_dialog.checkout.already_paid_title') || 'Already paid',
          message:
            t('alert_dialog.checkout.already_paid_message') ||
            'This booking has already been paid.',
          primaryAction: {
            label: t('alert_dialog.checkout.already_paid_primary_action') || 'OK',
          },
        });
        return;
      }

      if (status === 409 && data.code === 'CHECKOUT_PENDING') {
        try { dispatch(getBrandEvents()); } catch {}
        setCheckoutError(
          data.message ||
            t('dashboard.multibanco_pending_body') ||
            'A payment checkout is already pending for this booking.'
        );
        showAlert({
          type: 'info',
          title:
            t('dashboard.multibanco_pending_title') ||
            'Multibanco payment pending',
          message:
            data.message ||
            t('dashboard.multibanco_pending_body') ||
            'A payment checkout is already pending for this booking.',
          primaryAction: {
            label: t('common.ok') || 'OK',
          },
        });
        return;
      }

      if (status === 410 && data.code === 'NOT_PAYABLE') {
        // Booking is no longer payable (expired/rejected/event closed) — refetch
        // so the UI reflects its actual state instead of a generic failure.
        try { dispatch(getBrandEvents()); } catch {}
        showAlert({
          type: 'error',
          title: t('alert_dialog.checkout.not_payable_title') || 'Payment unavailable',
          message:
            data.message ||
            t('alert_dialog.checkout.not_payable_message') ||
            'This booking can no longer be paid.',
          primaryAction: {
            label: t('alert_dialog.checkout.not_payable_primary_action') || 'OK',
          },
        });
        return;
      }

      console.error('Checkout error:', err);
      setCheckoutError(
        data.message || t('alert_dialog.checkout.payment_failed_message')
      );
      showAlert({
        type: 'error',
        title: t('alert_dialog.checkout.payment_failed_title'),
        message:
          data.message || t('alert_dialog.checkout.payment_failed_message'),
        primaryAction: {
          label: t('alert_dialog.checkout.payment_failed_primary_action'),
        },
      });
    }
  };


  const renderDeadlinePill = () => {
    if (!(status === 'Pending' || status === 'Approved')) return null;

    // Priority: explicit overdue; then event is over; else show live countdown
    const pillClass =
      isOverdue || isEventOver
        ? 'bg-red-100 text-red-700'
        : 'bg-yellow-100 text-yellow-700';

    let label;
    if (status === 'Pending') {
      label = t('dashboard.manager_modal.time_left_for_approval');
    } else {
      label = t('dashboard.manager_modal.time_left_to_pay');
    }

    const right = isOverdue
      ? t('dashboard.manager_modal.expired')
      : isEventOver
        ? t('dashboard.manager_modal.event_ended')
        : fmtTimeLeft();

    if (!right) return null;

    const deadlineText =
      status === 'Approved' && !isOverdue && !isEventOver
        ? fmtDeadlineDate(expireAt)
        : null;

    return (
      <div className="flex flex-col items-center gap-1">
        <div
          className={`px-3 py-1 rounded-full text-xs sm:text-sm font-semibold ${pillClass}`}
        >
          {label} <span className="font-bold">{right}</span>
        </div>
        {deadlineText && (
          <p className="text-xs text-gray-500">
            {t('dashboard.manager_modal.pay_by')}{' '}
            <b>{deadlineText}</b>
          </p>
        )}
      </div>
    );
  };

  const eventWindowText = useMemo(() => {
    if (!eventStart || !eventEnd) return null;
    const fmt = (m) => m.local().format('LLL'); // locale-aware
    return `${fmt(eventStart)} → ${fmt(eventEnd)}`;
  }, [eventStart, eventEnd]);

  return (
    <div className="relative space-y-6 p-4 sm:p-6">
      {/* Hero card — status-driven headline */}
      <div className="max-w-3xl mx-auto w-full">
        <div className="rounded-2xl bg-gradient-to-br from-brand-soft to-white ring-1 ring-amber-200 shadow-sm p-6 text-center">
          <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider">
            {t('dashboard.em.your_booking')}
          </p>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mt-2">
            {(() => {
              if (isPaid) return t('dashboard.em.status_hero_paid');
              if (status === 'Approved')
                return t('dashboard.em.status_hero_approved');
              if (status === 'Rejected')
                return t('dashboard.em.status_hero_rejected');
              if (status === 'Expired')
                return t('dashboard.em.status_hero_expired');
              return t('dashboard.em.status_hero_pending');
            })()}
          </h3>
          {eventWindowText && (
            <p className="text-xs text-gray-500 mt-2">
              {t('dashboard.manager_modal.event_window')}: <b>{eventWindowText}</b>
            </p>
          )}
          <div className="mt-3 flex justify-center">{renderDeadlinePill()}</div>
        </div>
      </div>

      {/* Progress steps — vertical list */}
      {status !== 'Expired' ? (
        <div className="max-w-3xl mx-auto w-full">
          <div className="rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-6">
            <h4 className="text-sm font-bold text-gray-900 mb-4">
              {t('dashboard.em.progress')}
            </h4>
            <ol className="space-y-4">
              {[
                {
                  key: 'Pending',
                  label: t('dashboard.em.progress_step_request'),
                  hint: t('dashboard.em.progress_step_request_hint'),
                },
                {
                  key: 'Approved',
                  label: t('dashboard.em.progress_step_approved'),
                  hint: t('dashboard.em.progress_step_approved_hint'),
                },
                {
                  key: 'Paid',
                  label: t('dashboard.em.progress_step_paid'),
                },
              ].map((step, i) => {
                const currentIdx = isPaid
                  ? 2
                  : status === 'Approved'
                    ? 1
                    : 0;
                const done = i < currentIdx || (isPaid && i === currentIdx);
                const current = !done && i === currentIdx;
                return (
                  <li key={step.key} className="flex items-start gap-3">
                    <div
                      className={[
                        'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ring-2',
                        done &&
                          (step.key === 'Paid'
                            ? 'bg-blue-500 text-white ring-blue-100'
                            : 'bg-emerald-500 text-white ring-emerald-100'),
                        current && 'bg-brand text-white ring-brand-soft',
                        !done &&
                          !current &&
                          'bg-gray-100 text-gray-400 ring-gray-50',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {done ? '✓' : i + 1}
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <p
                        className={`text-sm font-semibold ${
                          done || current ? 'text-gray-900' : 'text-gray-400'
                        }`}
                      >
                        {step.label}
                      </p>
                      {step.hint && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {step.hint}
                        </p>
                      )}
                    </div>
                    {current && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 pt-1">
                        {t('dashboard.em.current')}
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      ) : (
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            {t(
              'dashboard.booking_expired_rebook',
              'If you’re still interested, feel free to request a new booking for this event.'
            )}
          </p>
        </div>
      )}


      {/* Payment processing indicator (webhook pending) */}
      {isPaymentProcessing && status === 'Approved' && (
        <div className="flex justify-center mt-4 sm:mt-6">
          <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-sm sm:text-base font-medium">
            <ClipLoader size={16} color="#d97706" />
            <span>
              {t(
                'dashboard.payment_processing',
                'Your payment is being processed — this usually takes a few moments.'
              )}
            </span>
          </div>
        </div>
      )}

      {/* Price breakdown (shown before Pay Now) */}
      {status === 'Approved' &&
        !isPaid &&
        !isPaymentProcessing &&
        pricing.total > 0 && (
        <div className="flex justify-center">
          <div className="w-full max-w-sm bg-white border border-gray-200 rounded-xl p-4 space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>{t('booking.space_price')}</span>
              <span>{fmtEUR(pricing.price)}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>
                {t('event_detail.t2b_fee')}
                <FeeBoundsTooltip className="ml-1" />
              </span>
              <span>{fmtEUR(pricing.t2bFee)}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>{t('event_detail.vat')}</span>
              <span>{fmtEUR(pricing.vat)}</span>
            </div>
            <div className="border-t border-gray-200 pt-2 flex justify-between font-bold text-gray-900">
              <span>{t('booking.total')}</span>
              <span>{fmtEUR(pricing.total)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Multibanco pending — replaces the Pay CTA while a reference is active. */}
      {status === 'Approved' &&
        !isPaid &&
        !isPaymentProcessing &&
        pendingMultibanco && (
        <div ref={paymentSectionRef} className="flex justify-center mt-4 sm:mt-6">
          <div className="w-full max-w-sm bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
            <p className="font-semibold mb-2">
              {t('dashboard.multibanco_pending_title') ||
                'Multibanco payment pending'}
            </p>
            <p className="mb-3">
              {t('dashboard.multibanco_pending_body', {
                expiration: pendingMultibanco.expiration_date
                  ? new Date(pendingMultibanco.expiration_date).toLocaleString(
                      i18n?.language || 'pt-PT'
                    )
                  : '',
              }) ||
                `Complete your payment at any ATM using the reference below${
                  pendingMultibanco.expiration_date
                    ? ` before ${new Date(
                        pendingMultibanco.expiration_date
                      ).toLocaleString(i18n?.language || 'pt-PT')}`
                    : ''
                }.`}
            </p>
            {(pendingMultibanco.entity ||
              pendingMultibanco.reference ||
              pendingMultibanco.value != null) && (
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs font-mono">
                {pendingMultibanco.entity && (
                  <>
                    <dt className="text-amber-800/70">
                      {t('checkout.multibanco_entity') || 'Entity'}
                    </dt>
                    <dd className="font-semibold">
                      {pendingMultibanco.entity}
                    </dd>
                  </>
                )}
                {pendingMultibanco.reference && (
                  <>
                    <dt className="text-amber-800/70">
                      {t('checkout.multibanco_reference') || 'Reference'}
                    </dt>
                    <dd className="font-semibold">
                      {pendingMultibanco.reference}
                    </dd>
                  </>
                )}
                {pendingMultibanco.value != null && (
                  <>
                    <dt className="text-amber-800/70">
                      {t('checkout.multibanco_amount') || 'Amount'}
                    </dt>
                    <dd className="font-semibold">
                      {fmtEUR(Number(pendingMultibanco.value))}
                    </dd>
                  </>
                )}
                {pendingMbExpiresAt?.isValid() && (
                  <>
                    <dt className="text-amber-800/70">
                      {t('checkout.multibanco_expires') ||
                        t('dashboard.manager_modal.pay_by')}
                    </dt>
                    <dd
                      className={`font-semibold ${
                        pendingMbIsExpired ? 'text-red-700' : ''
                      }`}
                    >
                      {fmtDeadlineDate(pendingMbExpiresAt)}
                    </dd>
                  </>
                )}
                {pendingMbExpiresAt?.isValid() && fmtPendingMbTimeLeft() && (
                  <>
                    <dt className="text-amber-800/70">
                      {t('dashboard.manager_modal.time_left')}
                    </dt>
                    <dd
                      className={`font-semibold ${
                        pendingMbIsExpired ? 'text-red-700' : ''
                      }`}
                    >
                      {fmtPendingMbTimeLeft()}
                    </dd>
                  </>
                )}
                {!pendingMbExpiresAt?.isValid() && expireAt && fmtTimeLeft() && (
                  <>
                    <dt className="text-amber-800/70">
                      {t('dashboard.manager_modal.time_left')}
                    </dt>
                    <dd
                      className={`font-semibold ${
                        isOverdue ? 'text-red-700' : ''
                      }`}
                    >
                      {fmtTimeLeft()}
                    </dd>
                  </>
                )}
              </dl>
            )}
            {pendingMultibanco.checkoutId && (
              <button
                type="button"
                onClick={viewPendingMultibanco}
                className="mt-3 text-xs underline text-amber-900 hover:text-amber-950"
              >
                {t('dashboard.multibanco_view_reference') ||
                  'View full reference'}
              </button>
            )}
          </div>
        </div>
      )}

      {status === 'Approved' &&
        !isPaid &&
        !isPaymentProcessing &&
        blockedActiveCheckout && (
        <div className="flex justify-center mt-4 sm:mt-6">
          <div className="w-full max-w-sm bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
            <p className="font-semibold mb-2">
              {t('dashboard.multibanco_pending_title') ||
                'Multibanco payment pending'}
            </p>
            <p>
              {t('dashboard.checkout_pending_recovery_body')}
            </p>
          </div>
        </div>
      )}

      {/* Payment CTA (hidden while processing or while MB pending) */}
      {status === 'Approved' &&
        !isPaid &&
        !isPaymentProcessing &&
        !pendingMultibanco &&
        !blockedActiveCheckout && (
        paymentStep === 'billing' ? (
          <form
            ref={paymentSectionRef}
            onSubmit={handleBillingSubmit}
            noValidate
            className="mx-auto mt-4 sm:mt-6 w-full max-w-md bg-white border border-gray-200 rounded-xl p-5 space-y-4 text-left shadow-sm"
          >
            <div>
              <h3 className="text-base font-bold text-gray-900">
                {t('checkout.billing_title')}
              </h3>
              <p className="text-sm text-gray-500">
                {t('checkout.billing_subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="space-y-1 text-sm font-semibold text-gray-700">
                <span className="flex items-center justify-between gap-2">
                  <span>{t('checkout.billing_name')}</span>
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-red-600">
                    {t('checkout.billing_required')}
                  </span>
                </span>
                <input
                  type="text"
                  value={billingForm.name}
                  onChange={(e) => updateBillingField('name', e.target.value)}
                  className={`w-full px-3 py-2.5 rounded-lg border bg-white outline-none transition placeholder:text-gray-400 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/30 ${
                    billingErrors.name ? 'border-red-400' : 'border-gray-300'
                  }`}
                  autoComplete="name"
                  aria-invalid={!!billingErrors.name}
                  autoFocus
                  required
                />
                {billingErrors.name && (
                  <span className="block text-xs text-red-600">
                    {billingErrors.name}
                  </span>
                )}
              </label>

              <label className="space-y-1 text-sm font-semibold text-gray-700">
                <span className="flex items-center justify-between gap-2">
                  <span>{t('checkout.billing_nif')}</span>
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-red-600">
                    {t('checkout.billing_required')}
                  </span>
                </span>
                <input
                  type="text"
                  value={billingForm.nif}
                  onChange={(e) => updateBillingField('nif', e.target.value)}
                  className={`w-full px-3 py-2.5 rounded-lg border bg-white outline-none transition placeholder:text-gray-400 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/30 ${
                    billingErrors.nif ? 'border-red-400' : 'border-gray-300'
                  }`}
                  placeholder={t('checkout.billing_nif_placeholder')}
                  inputMode="numeric"
                  autoComplete="off"
                  aria-invalid={!!billingErrors.nif}
                  required
                />
                {billingErrors.nif && (
                  <span className="block text-xs text-red-600">
                    {billingErrors.nif}
                  </span>
                )}
              </label>
            </div>

            <label className="block space-y-1 text-sm font-semibold text-gray-700">
              <span className="flex items-center justify-between gap-2">
                <span>{t('checkout.billing_address')}</span>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {t('checkout.billing_optional')}
                </span>
              </span>
              <input
                type="text"
                value={billingForm.address}
                onChange={(e) => updateBillingField('address', e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-300 bg-white outline-none transition placeholder:text-gray-400 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/30"
                placeholder={t('checkout.billing_address_placeholder')}
                autoComplete="street-address"
              />
            </label>

            <label className="block space-y-1 text-sm font-semibold text-gray-700">
              <span className="flex items-center justify-between gap-2">
                <span>{t('checkout.billing_mobile')}</span>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {t('checkout.billing_optional')}
                </span>
              </span>
              <input
                type="tel"
                value={billingForm.contact}
                onChange={(e) => updateBillingField('contact', e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-300 bg-white outline-none transition placeholder:text-gray-400 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/30"
                placeholder={t('checkout.billing_mobile_placeholder')}
                inputMode="tel"
                autoComplete="tel"
              />
            </label>

            {checkoutError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {checkoutError}
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setPaymentStep('summary');
                  setCheckoutError(null);
                  setBillingErrors({});
                }}
                className="px-4 py-2 rounded-xl border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                {t('checkout.billing_back')}
              </button>
              <button
                type="submit"
                className={`px-4 py-2 rounded-xl text-sm font-semibold text-white ${
                  loading
                    ? 'bg-blue-300 cursor-not-allowed'
                    : 'bg-blue-500 hover:bg-blue-600'
                }`}
                disabled={loading}
              >
                {loading
                  ? t('dashboard.loading_payment')
                  : t('checkout.billing_continue')}
              </button>
            </div>
          </form>
        ) : (
          <div ref={paymentSectionRef} className="flex justify-center mt-4 sm:mt-6">
            <button
              onClick={handleStartCheckout}
              className={`font-semibold py-2 px-6 rounded-xl text-sm sm:text-base ${
                loading || !canPay
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
              disabled={loading || !canPay}
            >
              {!canPay
                ? isOverdue
                  ? t('dashboard.manager_modal.payment_window_expired')
                  : isEventOver
                    ? t('dashboard.event_ended')
                    : t('dashboard.unavailable')
                : loading
                  ? t('dashboard.loading_payment')
                  : t('dashboard.pay_now')}
            </button>
          </div>
        )
      )}

      {/* After payment */}
      {isPaid && (
        <div className="mt-6 bg-gray-100 border border-gray-200 rounded-xl p-4 space-y-4">
          <p className="text-sm text-gray-600">
            {t('dashboard.check_event_updates')}
          </p>
          {savedBillingInfo && (
            <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm">
              <p className="font-semibold text-gray-800 mb-2">
                {t('checkout.billing_title')}
              </p>
              <dl className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                {savedBillingInfo.name && (
                  <>
                    <dt className="font-semibold text-gray-500">
                      {t('checkout.billing_name')}
                    </dt>
                    <dd className="text-gray-800">{savedBillingInfo.name}</dd>
                  </>
                )}
                {savedBillingInfo.nif && (
                  <>
                    <dt className="font-semibold text-gray-500">
                      {t('checkout.billing_nif')}
                    </dt>
                    <dd className="text-gray-800">{savedBillingInfo.nif}</dd>
                  </>
                )}
                {savedBillingInfo.address && (
                  <>
                    <dt className="font-semibold text-gray-500">
                      {t('checkout.billing_address')}
                    </dt>
                    <dd className="text-gray-800">
                      {savedBillingInfo.address}
                    </dd>
                  </>
                )}
                {savedBillingInfo.contact && (
                  <>
                    <dt className="font-semibold text-gray-500">
                      {t('checkout.billing_mobile')}
                    </dt>
                    <dd className="text-gray-800">
                      {savedBillingInfo.contact}
                    </dd>
                  </>
                )}
              </dl>
            </div>
          )}
        </div>
      )}

      {/* Explicit Expired/Rejected info */}
      {(status === 'Expired' || status === 'Rejected') && (
        <div className="mt-4 text-center text-sm text-gray-500">
          {status === 'Expired'
            ? t('dashboard.manager_modal.booking_expired_info')
            : t('dashboard.manager_modal.booking_rejected_info')}
        </div>
      )}

      {/* Uploaded Documents */}
      {booking?.documents?.length > 0 && (
        <div className="mt-4 bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <DocumentIcon className="w-4 h-4 text-yellow-500 flex-shrink-0" />
            {t('booking.uploaded_documents')}
          </h3>
          <ul className="space-y-2">
            {booking.documents.map((doc, idx) => (
              <li key={idx}>
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-yellow-600 hover:text-yellow-700 font-medium hover:underline"
                >
                  <ArrowTopRightOnSquareIcon className="w-4 h-4 flex-shrink-0" />
                  {doc.name}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default BookingStatus;
